import React, { useContext, useEffect, useState, useMemo } from "react";
import { SlidableMenu } from "../../components/slidableMenu/SlidableMenu";
import { BottomMenuItems, bottomMenu as bottomMenuItems} from "../../mocks/dashboard";
import { AddClient } from "./components/addClient/AddClient";
import { BottomMenu } from "./components/bottomMenu/BottomMenu";
import { DashboardHead } from "./components/dashboardHead/DashboardHead";
import { Desktop } from "./components/desktop/Desktop";
import { SideMenuRouter } from "./components/sideMenu/SideMenuRouter";
import { DashboardClientPopupRouter } from "./DashboardClientPopupRouter";
import { DashboardMain } from "./DashboardMain";
import { DashboardContext } from "./DashboardContext";
import { AppContext } from "../../appContext";
import { formatDate1, setTimezoneAwareHours } from "../../utils/dateFormats";
import { ISession } from "../../types/dashboard";
import { CalendarGrid } from "../../components/calendarGrid/CalendarGrid";
import { Spinner } from "../../components/spinner/Spinner";
import { GoalsWidget } from "./components/goalsWidget/GoalsWidget";
import { TaskList } from "./components/taskList/TaskList";
import { NotesWidget } from "./components/notesWidget/NotesWidget";
import { AnalysisWidget } from "./components/analysisWidget/AnalysisWidget";

const TotalCalendar = ()=>{
    const {loadAllSessions} = useContext(AppContext);
    const [sessions, setSessions] = useState<ISession[]>();
    useEffect(()=>{
        loadAllSessions().then(res=>setSessions(res));
    }, []);

    return sessions ? <CalendarGrid
        tickets={sessions.map(session => {
            const startDate = new Date(session.date);
            let endDate = new Date(session.dateTo);
            if (!session.dateTo || Number.isNaN(endDate.valueOf())) {
                const startDateObj = new Date(session.date);
                const endHour = new Date(startDateObj).getHours() + 1;
                endDate = setTimezoneAwareHours(startDateObj, endHour); //default one hour if not end date
            }
            return {
                start: startDate,
                end: endDate,
                title: session.title,
                type: 'session',
                original: session
            }
        })}
    ></CalendarGrid> : <div style={{ padding: "16px 22px" }}> <Spinner></Spinner></div>
}

export const DashboardRightRouter = ()=>{
    const dashboardContext = useContext(DashboardContext);
    const {
        selectedClientId, setSelectedClientId,
        selectedClient,
        selectedBottomMenuTabId, setSelectedBottomMenuTabId,
        activeTag, setActiveTag,
        isDashWrap, setDashWrap,
        activePopup, setActivePopup,
        addClient,
        deleteClient: _deleteClient,
        deleteSession: _deleteSession,
        sideSelected,
        setSideSelected,
        bottomSelected,
        isDashboardVisible,
        setIsDashboardVisible,
        selectedTaskIndex, setSelectedTaskIndex,
        selectedFolderIndex, setSelectedFolderIndex,
        setTaskList
    } = dashboardContext;

    const contextData = useContext(AppContext);
    const clientsData = contextData.clients || [];
    const translate = contextData.translate;
    const currentLanguage = contextData.currentLanguage;

    const bottomMenu = useMemo(()=>{
        if (contextData.cheat.debug) {
            return bottomMenuItems.map(it=>it.name);
        }
        return bottomMenuItems.filter(it=>it.show == true).map(it=>it.name);
    }, [bottomMenuItems, contextData.cheat.debug])

    const currentRoute = useMemo(()=>{
        let result = ''
        if (selectedClientId == null || !selectedClient){
            if (activePopup!='AddClient'){
                result = 'desktop'
            }
        } else {
            result = 'client'
        }

        if ( activePopup == 'AddClient' ){
            result = 'addClient'
        }

        if ( activePopup == 'TotalCalendar' ){
            result = 'totalCalendar'
        }

        if (sideSelected && bottomSelected == 'avatar') {
            result = 'sideMenu'
        }

        console.log(result);

        return result;
    }, [selectedClientId, clientsData, activePopup, sideSelected, bottomSelected]);

    const clients = useMemo(()=>clientsData.map(it=>{
        return {
            id: it.id,
            name: it.name,
            date1: it.sessions?.[0] ? formatDate1(new Date(it.sessions[0]?.date), translate, currentLanguage) : '',
            date2: it.sessions?.[1] ? formatDate1(new Date(it.sessions[1]?.date), translate, currentLanguage) : '',
            avatarUrl: it.avatarUrl,
            waitLoading: it.waitLoading
        }
    }), [clientsData, translate, currentLanguage]);

    const slidableMenu = true;

    const clientView = <>
        <DashboardHead
            activeTag={activeTag}
            setActiveTag={setActiveTag}
            onWrapClick={() => { setDashWrap(last => !last) }}
            isWrap={isDashWrap}
            client={clients.find(it=>it.id == selectedClientId)}
        ></DashboardHead>
        
        {/* Dashboard collapsible panel - slides down when visible */}
        {selectedClient && (
            <div className={`dashboard_panel ${isDashboardVisible ? 'dashboard_panel--visible' : ''}`}>
                <div className="dashboard_panel_content">
                    <div className="dashboard_main_left">
                        <GoalsWidget />
                        <TaskList></TaskList>
                    </div>
                    <div className="dashboard_main_right">
                        <NotesWidget />
                        <AnalysisWidget />
                    </div>
                </div>
            </div>
        )}
        
        {/* Main content with sessions - wrapped in under_layer for height animation */}
        <div className={`dashboard_main_drop ${!isDashWrap ? '' : 'dashboard_main_drop--opened'}`}>
            <div className={`under_layer ${isDashboardVisible ? 'under_layer--hidden' : ''}`}>
                <DashboardMain></DashboardMain>
            </div>
        </div>

        {!slidableMenu ? <BottomMenu
            bottomMenu={bottomMenu}
            selectedBottomMenuTabIndex={bottomMenu.findIndex(it=>it == selectedBottomMenuTabId)}
            setSelectedBottomMenuTabIndex={(index)=>{
                const clickedTab = bottomMenu[index] as BottomMenuItems;
                if (isDashboardVisible) {
                    setIsDashboardVisible(false);
                }
                setSelectedBottomMenuTabId(clickedTab);
                setDashWrap(false);
            }}
        ></BottomMenu> :
            <SlidableMenu
                items={bottomMenu.map(it=>translate('bottomMenu_'+it as any))}
                selectedItemIndex={bottomMenu.findIndex(it=>it == selectedBottomMenuTabId)}
                onSelect={(index)=>{
                    const clickedTab = bottomMenu[index] as BottomMenuItems;
                    if (isDashboardVisible) {
                        setIsDashboardVisible(false);
                    }
                    setSelectedBottomMenuTabId(clickedTab);
                    setDashWrap(false);
                }}
            ></SlidableMenu>
        }
        <DashboardClientPopupRouter></DashboardClientPopupRouter>
    </>

    const addClientView = (
        <AddClient 
            onClose={()=>setActivePopup('')} 
            onSave={(data)=>{   
                addClient(data).then(()=>{
                   setActivePopup(''); 
                })
            }} 
            onSaveDraft={()=>{}}
        ></AddClient>
    );

    const views = {
        desktop: <Desktop></Desktop>,
        client: clientView,
        addClient: addClientView,
        totalCalendar: <TotalCalendar></TotalCalendar>,
        sideMenu: <SideMenuRouter selected={sideSelected} onSelect={(name)=>{
            setSideSelected(name)
        }} />
    };

    const currentView = views[currentRoute as keyof typeof views];
    return (
        <>
            {currentRoute ? currentView: ''}
        </>
    )
}